import { z } from "zod";
import { MealPlanSchema } from "./rooms";

// ─── Booking Status ───────────────────────────────────────────────────────────

export const BookingStatusSchema = z.enum([
  "pending",
  "confirmed",
  "checked_in",
  "checked_out",
  "cancelled",
  "refunded",
]);
export type BookingStatus = z.infer<typeof BookingStatusSchema>;

// ─── Price Breakdown ──────────────────────────────────────────────────────────

export const PriceBreakdownSchema = z.object({
  basePrice: z.number(), // price per night for selected meal plan
  nights: z.number(),
  subtotal: z.number(),
  gstRate: z.number(), // 5 or 18 — dynamically calculated
  gstAmount: z.number(),
  total: z.number(),
  mealPlan: MealPlanSchema.optional(),
});
export type PriceBreakdown = z.infer<typeof PriceBreakdownSchema>;

// ─── Selected Addon ───────────────────────────────────────────────────────────

export const SelectedAddonSchema = z.object({
  addonId: z.string(),
  name: z.string(),
  price: z.number(),
  quantity: z.number(),
  unit: z.string(),
  lineTotal: z.number(),
});
export type SelectedAddon = z.infer<typeof SelectedAddonSchema>;

// ─── Guest Details ────────────────────────────────────────────────────────────

export const GuestDetailsSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Enter a valid email"),
  phone: z
    .string()
    .min(10, "Enter a valid phone number")
    .max(15, "Enter a valid phone number"),
  address: z.string().optional(),
  specialRequests: z.string().optional(),
});
export type GuestDetails = z.infer<typeof GuestDetailsSchema>;

// ─── Booking Document (stored in Firestore) ───────────────────────────────────

export const BookingSchema = z.object({
  id: z.string(),
  bookingRef: z.string(),
  roomTypeId: z.string(),
  roomTypeName: z.string(),
  roomId: z.string(),
  checkIn: z.string(),
  checkOut: z.string(),
  nights: z.number(),
  guests: z.object({
    adults: z.number(),
    children: z.number(),
  }),
  guest: GuestDetailsSchema,
  pricing: PriceBreakdownSchema,
  mealPlan: MealPlanSchema.optional(),
  addons: z.array(SelectedAddonSchema).default([]),
  addonsTotal: z.number().default(0),
  status: BookingStatusSchema,
  razorpayOrderId: z.string().optional(),
  razorpayPaymentId: z.string().optional(),
  guestUid: z.string().optional(),
  notes: z.string().default(""),
  createdAt: z.any().optional(),
  updatedAt: z.any().optional(),
});
export type Booking = z.infer<typeof BookingSchema>;

// ─── Booking flow state (client-side, across steps) ──────────────────────────

export interface BookingDraft {
  checkIn: string;
  checkOut: string;
  adults: number;
  children: number;
  roomTypeId: string;
  roomTypeName: string;
  mealPlan: import("./rooms").MealPlan | null;
  pricing: PriceBreakdown | null;
  addons: SelectedAddon[];
  addonsTotal: number;
  guest: GuestDetails | null;
  razorpayOrderId: string;
}

// ─── Availability ─────────────────────────────────────────────────────────────

export interface AvailableRoomType {
  roomTypeId: string;
  roomTypeName: string;
  availableCount: number;
  basePrice: number;
  gstRate: number;
  pricing: PriceBreakdown;
  mealPlanPricing?: import("./rooms").MealPlanPricing;
}

// ─── Cloud Function payloads ──────────────────────────────────────────────────

export interface CheckAvailabilityPayload {
  checkIn: string;
  checkOut: string;
  adults: number;
  children: number;
}

export interface CheckAvailabilityResponse {
  available: AvailableRoomType[];
}

export interface CreateOrderPayload {
  roomTypeId: string;
  checkIn: string;
  checkOut: string;
  adults: number;
  children: number;
  guest: GuestDetails;
  addons: SelectedAddon[];
  mealPlan?: import("./rooms").MealPlan;
}

export interface CreateOrderResponse {
  orderId: string;
  amount: number;
  currency: string;
  bookingRef: string;
  bookingId: string;
}